import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Inbox, Mail, Phone, FileText, ExternalLink, CheckCircle2, RefreshCw } from "lucide-react";

const STATUS_ORDER = ["pending", "contacted", "in_progress", "completed", "cancelled"];

const STATUS_BADGE = {
  pending:      "bg-amber-100 text-amber-900 border-amber-500",
  contacted:    "bg-blue-100 text-blue-900 border-blue-500",
  in_progress:  "bg-indigo-100 text-indigo-900 border-indigo-500",
  completed:    "bg-emerald-100 text-emerald-900 border-emerald-500",
  cancelled:    "bg-zinc-200 text-zinc-800 border-zinc-500",
};

export default function AdminReviews() {
  const { user, ready } = useAuth();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("pending");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/expert-reviews");
      setItems(data);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { all: items?.length || 0 };
    for (const s of STATUS_ORDER) c[s] = 0;
    (items || []).forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [items]);

  if (!ready) return null;
  if (!user || user.role !== "admin") return <Navigate to="/app" replace />;

  const filtered = (items || []).filter((r) => tab === "all" ? true : r.status === tab);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Admin · Inbox</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight mt-2 flex items-center gap-3">
              <Inbox className="w-7 h-7 text-[#0F172A]" />
              Expert-review queue
            </h1>
            <p className="text-sm text-zinc-600 mt-1">Paid £100 requests waiting for your team's manual verdict.</p>
          </div>
          <Button onClick={load} variant="outline" className="rounded-sm" data-testid="admin-refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="rounded-sm bg-zinc-100 p-1 flex-wrap h-auto">
            {["pending", "contacted", "in_progress", "completed", "cancelled", "all"].map((s) => (
              <TabsTrigger key={s} value={s}
                           className="rounded-sm data-[state=active]:bg-white capitalize"
                           data-testid={`admin-tab-${s}`}>
                {s.replace("_", " ")} <span className="ml-2 font-mono text-[10px] text-zinc-500">{counts[s] || 0}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={tab} className="mt-6">
            {items === null ? (
              <div className="p-10 text-center text-zinc-500 font-mono text-sm">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center border border-zinc-200 bg-white rounded-sm" data-testid="admin-empty">
                <Inbox className="w-8 h-8 mx-auto text-zinc-400" />
                <div className="font-heading text-xl font-semibold mt-3">Nothing here</div>
                <div className="text-sm text-zinc-500 mt-1">No requests with this status.</div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-reviews-list">
                {filtered.map((r) => (
                  <ReviewCard key={r.id} review={r}
                              expanded={expanded === r.id}
                              onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
                              onUpdated={(updated) => setItems((prev) => prev.map((x) => x.id === updated.id ? updated : x))} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function ReviewCard({ review, expanded, onToggle, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState(review.internal_notes || "");

  const patch = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/expert-reviews/${review.id}`, body);
      onUpdated(data);
      toast.success("Updated");
    } catch (e) {
      toast.error("Update failed");
    } finally { setSaving(false); }
  };

  const badge = STATUS_BADGE[review.status] || STATUS_BADGE.pending;

  return (
    <div className="border border-zinc-200 bg-white rounded-sm" data-testid={`admin-review-${review.id}`}>
      <button className="w-full text-left px-5 py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center hover:bg-zinc-50"
              onClick={onToggle}>
        <div className="md:col-span-3 min-w-0">
          <div className="font-heading font-semibold truncate">{review.contact_name}</div>
          <div className="font-mono text-xs text-zinc-500 truncate">{review.contact_email}</div>
        </div>
        <div className="md:col-span-2 font-mono text-xs text-zinc-600 truncate">
          {review.contact_phone || "—"}
        </div>
        <div className="md:col-span-3 font-mono text-xs text-zinc-500 truncate">
          {new Date(review.created_at).toLocaleString()}
        </div>
        <div className="md:col-span-2 font-mono text-sm font-bold">
          £{review.amount_paid} {review.currency?.toUpperCase()}
        </div>
        <div className="md:col-span-2 flex md:justify-end">
          <span className={`inline-flex items-center px-2 py-1 rounded-sm border font-mono text-[10px] uppercase tracking-[0.15em] ${badge}`}>
            {review.status?.replace("_", " ")}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-zinc-200 px-5 py-5 grid lg:grid-cols-12 gap-6 bg-zinc-50/40">
          <div className="lg:col-span-5 space-y-3 text-sm">
            <Row icon={Mail} label="Email" value={<a href={`mailto:${review.contact_email}`} className="underline underline-offset-4">{review.contact_email}</a>} />
            <Row icon={Phone} label="Phone" value={review.contact_phone || "—"} />
            <Row icon={FileText} label="Notes from client"
                 value={<span className="text-zinc-800">{review.notes || <span className="text-zinc-400">None</span>}</span>} />
            {review.check_id && (
              <Row icon={ExternalLink} label="Linked check"
                   value={
                     <Link to={`/app/result/${review.check_id}`} target="_blank"
                           className="underline underline-offset-4 font-mono text-xs" data-testid={`admin-open-check-${review.id}`}>
                       Open result →
                     </Link>
                   } />
            )}
            <div className="pt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              User: {review.user_email} · session {review.session_id?.slice(0, 14)}…
              {review.handled_by && <> · handled by {review.handled_by}</>}
            </div>
          </div>

          <div className="lg:col-span-7 space-y-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Internal notes</div>
              <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                        className="rounded-sm mt-1"
                        placeholder="Findings, next steps, follow-up plan…"
                        data-testid={`admin-notes-${review.id}`} />
              <div className="mt-2 flex justify-end">
                <Button size="sm" variant="outline" className="rounded-sm" disabled={saving}
                        onClick={() => patch({ internal_notes: notes })}
                        data-testid={`admin-save-notes-${review.id}`}>
                  Save notes
                </Button>
              </div>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Move to</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {STATUS_ORDER.filter((s) => s !== review.status).map((s) => (
                  <Button key={s} size="sm" variant="outline" className="rounded-sm capitalize"
                          disabled={saving}
                          onClick={() => patch({ status: s })}
                          data-testid={`admin-set-${s}-${review.id}`}>
                    {s === "completed" && <CheckCircle2 className="w-3.5 h-3.5" />}
                    {s.replace("_", " ")}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <div className="mt-0.5 text-sm text-zinc-800 break-words">{value}</div>
      </div>
    </div>
  );
}
